import React, { useEffect, useRef, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Switch } from '@headlessui/react';
import classNames from 'classnames';
import _ from 'lodash';
import {
  detectNumberedFormat,
  handleNumberedKeyDown,
  handleNumberedPaste,
  normalizeAndNumberLines,
} from '@/utils/numberedSteps';

export const LOCAL_STORAGE_KEY = 'qa-tools:step-issue-numbered';

interface NumberedStepsFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  className?: string;
  hasRequiredAsterisk?: boolean;
}

export const NumberedStepsField: React.FC<NumberedStepsFieldProps> = ({
  name,
  label = 'Steps to Reproduce',
  placeholder,
  className,
  hasRequiredAsterisk,
}) => {
  const { control, setValue, getValues, formState } = useFormContext();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Initialize toggle state according to rules:
  // - On Edit: if initial text has numbered steps, it overrides localStorage (ON). If not, OFF.
  // - On Create / empty: reads localStorage (default ON, try/catch).
  const [isNumbered, setIsNumbered] = useState<boolean>(() => {
    try {
      const initialVal = getValues(name);
      if (typeof initialVal === 'string' && initialVal.trim().length > 0) {
        return detectNumberedFormat(initialVal);
      }
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved !== null) {
        return saved === 'true';
      }
    } catch {
      // Fallback if localStorage throws error (AC12)
    }
    return true;
  });

  const isMountedRef = useRef(false);

  // When form value loads asynchronously in Edit mode
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      const currentVal = getValues(name);
      if (typeof currentVal === 'string' && currentVal.trim().length > 0) {
        setIsNumbered(detectNumberedFormat(currentVal));
      }
    }
  }, [getValues, name]);

  const handleToggleChange = (checked: boolean) => {
    setIsNumbered(checked);
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, String(checked));
    } catch {
      // Graceful fallback for localStorage failure
    }

    // AC6: When toggled OFF -> ON and textarea already has content, normalize all non-empty lines
    if (checked) {
      const currentVal = getValues(name) ?? '';
      if (typeof currentVal === 'string' && currentVal.trim().length > 0) {
        const numbered = normalizeAndNumberLines(currentVal);
        setValue(name, numbered, { shouldDirty: true });
      }
    }
    // AC7: When toggled ON -> OFF, keep existing text as-is without renumbering
  };

  return (
    <div className={classNames('flex flex-col w-full gap-1', className)}>
      <div className="flex items-center justify-between">
        <label
          htmlFor={name}
          className={classNames('font-semibold', {
            "after:content-['*'] after:text-red-400 after:text-sm": hasRequiredAsterisk,
          })}
        >
          {label}
        </label>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-normal">
            Numbered format (1, 2, 3…)
          </span>
          <Switch
            checked={isNumbered}
            onChange={handleToggleChange}
            aria-label="Numbered format (1, 2, 3…)"
            className={classNames(
              'group relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
              isNumbered ? 'bg-blue-600' : 'bg-gray-300',
            )}
          >
            <span
              className={classNames(
                'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out',
                isNumbered ? 'translate-x-4' : 'translate-x-0',
              )}
            />
          </Switch>
        </div>
      </div>

      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const value = typeof field.value === 'string' ? field.value : '';

          const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const raw = e.target.value;

            // AC1: When textarea was empty and user begins typing while toggle ON, prefix line with "1. "
            if (isNumbered && value === '' && raw.length > 0 && !raw.startsWith('1. ')) {
              const prefixed = `1. ${raw}`;
              field.onChange(prefixed);
              setTimeout(() => {
                if (textareaRef.current) {
                  textareaRef.current.setSelectionRange(prefixed.length, prefixed.length);
                }
              }, 0);
              return;
            }

            field.onChange(raw);
          };

          const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (!isNumbered) return;

            const target = e.currentTarget;
            const res = handleNumberedKeyDown(
              e,
              target.value,
              target.selectionStart,
              target.selectionEnd,
            );

            if (res.handled && res.newValue !== undefined) {
              field.onChange(res.newValue);
              if (res.newCursorPos !== undefined) {
                const nextPos = res.newCursorPos;
                setTimeout(() => {
                  if (textareaRef.current) {
                    textareaRef.current.setSelectionRange(nextPos, nextPos);
                  }
                }, 0);
              }
            }
          };

          const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
            if (!isNumbered) return;

            const pasted = e.clipboardData.getData('text');
            if (!pasted) return;

            // Normalize pasted text inserting at selection
            e.preventDefault();
            const target = e.currentTarget;
            const res = handleNumberedPaste(
              pasted,
              target.value,
              target.selectionStart,
              target.selectionEnd,
            );

            field.onChange(res.newValue);
            setTimeout(() => {
              if (textareaRef.current) {
                textareaRef.current.setSelectionRange(res.newCursorPos, res.newCursorPos);
              }
            }, 0);
          };

          return (
            <div>
              <textarea
                ref={elem => {
                  textareaRef.current = elem;
                  field.ref(elem);
                }}
                id={name}
                name={name}
                value={value}
                placeholder={placeholder}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                rows={4}
                className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />

              {_.get(formState.errors, name) && (
                <span className="mt-2 text-sm text-red-500">
                  {_.get(formState.errors, name)?.message?.toString()}
                </span>
              )}
            </div>
          );
        }}
      />
    </div>
  );
};
